import React from "react";
import { useNavigate } from "react-router-dom";
import { eur } from "@/lib/constants";
import { StatusBadge } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Wallet, Plus, ExternalLink, Mail, CheckCircle2, AlertTriangle, FileText, Boxes, Trash2, Save } from "lucide-react";
import SubRentalsRecap from "@/components/SubRentalsRecap";
import { useAuth } from "@/context/AuthContext";
import { CategoryTag, categoryColor } from "@/lib/categoryColors";

export const fmtDateTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (isNaN(d)) return "";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" }) +
    " à " + d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" }).replace(":", "h");
};

const ORANGE_BTN = "rounded-full bg-[#FF6A00] font-semibold text-white hover:bg-[#E65F00]";
const EMPTY = "rounded-xl border border-dashed border-border p-8 text-center text-muted-foreground";

function FinCard({ label, value, tone }) {
  const c = tone === "emerald" ? "text-emerald-500" : tone === "orange" ? "text-[#FF6A00]" : "text-foreground";
  return (
    <div className="rounded-xl border border-border bg-card p-4">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={`mt-1 font-display text-lg font-bold sm:text-xl ${c}`}>{value}</p>
    </div>
  );
}

function PayChip({ label, paid, at, amount }) {
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium ${paid ? "bg-emerald-500/15 text-emerald-500" : "bg-amber-500/15 text-amber-500"}`}>
      {paid ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
      {label} {paid ? `payé${at ? " le " + new Date(at).toLocaleDateString("fr-FR") : ""}` : `dû${amount ? " · " + eur(amount) : ""}`}
    </span>
  );
}

function QuoteRow({ q }) {
  const navigate = useNavigate();
  return (
    <button data-testid={`ev-quote-${q.id}`} onClick={() => navigate(`/app/devis/${q.id}`)}
      className="flex w-full items-center justify-between rounded-xl border border-border bg-card p-4 text-left transition-colors hover:border-[#FF6A00]/50">
      <div>
        <p className="font-medium text-foreground">{q.number || q.title || "Devis"}</p>
        <p className="text-xs text-muted-foreground">{q.total != null ? eur(q.total) : ""}{q.deposit_percent ? ` · Acompte ${q.deposit_percent}%` : ""}</p>
        <div className="mt-1.5 flex flex-wrap gap-1.5">
          <PayChip label="Acompte" paid={q.deposit_paid} at={q.deposit_paid_at} amount={q.deposit} />
          <PayChip label="Solde" paid={q.balance_paid} at={q.balance_paid_at} amount={q.balance} />
        </div>
      </div>
      <div className="flex items-center gap-2"><StatusBadge status={q.status} type="quote" /><ExternalLink className="h-4 w-4 text-muted-foreground" /></div>
    </button>
  );
}

export function PaymentsTab({ eventId, quotes, fin, balanceDue, onSendBalance, lastReminderAt, reminderCount }) {
  const navigate = useNavigate();
  return (
    <>
      <div className="mb-4 grid grid-cols-3 gap-3" data-testid="ev-fin-summary">
        <FinCard label="Total facturé" value={eur(fin.total)} />
        <FinCard label="Acompte reçu" value={eur(fin.received)} tone="emerald" />
        <FinCard label="Solde restant dû" value={eur(fin.due)} tone="orange" />
      </div>
      {balanceDue && (
        <Button data-testid="ev-send-balance" onClick={onSendBalance} className="mb-4 rounded-full bg-emerald-600 font-semibold text-white hover:bg-emerald-500"><Wallet className="mr-1.5 h-4 w-4" />Encaisser le solde — envoyer le lien de paiement au client</Button>
      )}
      {lastReminderAt && (
        <div className="mb-4 flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-2.5 text-sm text-muted-foreground" data-testid="ev-reminder-history">
          <Mail className="h-4 w-4 shrink-0 text-[#FF6A00]" />
          <span>Dernière relance de solde envoyée le <strong className="text-foreground">{fmtDateTime(lastReminderAt)}</strong>{reminderCount ? ` · ${reminderCount} relance${reminderCount > 1 ? "s" : ""} au total` : ""}</span>
        </div>
      )}
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Devis, factures et acomptes rattachés à cet événement.</p>
        <Button data-testid="ev-new-quote" onClick={() => navigate(`/app/devis/nouveau?event=${eventId}`)} className={ORANGE_BTN}><Plus className="mr-1 h-4 w-4" />Nouveau devis</Button>
      </div>
      {quotes?.length ? <div className="space-y-2">{quotes.map((q) => <QuoteRow key={q.id} q={q} />)}</div>
        : <p className={EMPTY} data-testid="ev-quotes-empty">Aucun devis rattaché. Créez-en un pour envoyer un devis, générer une facture et encaisser l'acompte.</p>}
    </>
  );
}

const RES_STATUS = {
  charge: ["Chargé", "bg-blue-500/10 text-blue-500"],
  retourne: ["Rendu", "bg-emerald-500/10 text-emerald-500"],
};

export function LogisticsTab({ eventId, quotes, reservations, onLoadingPdf, onReturnPdf }) {
  const { org } = useAuth();
  const navigate = useNavigate();
  return (
    <>
      <SubRentalsRecap eventId={eventId} refreshKey={(quotes || []).map((q) => `${q.id}:${q.status}`).join(",")} />
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <p className="text-sm text-muted-foreground">Matériel réservé à charger et à rendre pour cette prestation.</p>
        <div className="flex items-center gap-2">
          <Button data-testid="ev-loading-pdf" onClick={onLoadingPdf} className={ORANGE_BTN}><FileText className="mr-1 h-4 w-4" />PDF de chargement</Button>
          <Button data-testid="ev-return-pdf" onClick={onReturnPdf} variant="outline" className="rounded-full"><FileText className="mr-1 h-4 w-4" />PDF de retour</Button>
          <Button data-testid="ev-go-stock" onClick={() => navigate("/app/stock")} variant="outline" className="rounded-full"><Boxes className="mr-1 h-4 w-4" />Gérer le matériel</Button>
        </div>
      </div>
      {reservations?.length ? (
        <div className="space-y-2">
          {reservations.map((r) => {
            const [label, cls] = RES_STATUS[r.status] || ["Réservé", "bg-[#FF6A00]/10 text-[#FF6A00]"];
            return (
              <div key={r.id} data-testid={`ev-res-${r.id}`} className="flex items-center justify-between rounded-xl border border-border bg-card p-4 text-sm">
                <span className="flex min-w-0 flex-wrap items-center gap-2 text-foreground">{r.item_name} × {r.quantity}{categoryColor(org, r.item_category) && <CategoryTag org={org} name={r.item_category} testid={`ev-res-cat-${r.id}`} />}</span>
                <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${cls}`}>{label}</span>
              </div>
            );
          })}
        </div>
      ) : <p className={EMPTY} data-testid="ev-res-empty">Aucun matériel réservé pour cet événement.</p>}
    </>
  );
}

function StepCard({ st, i, onChange, onDelete }) {
  return (
    <div data-testid={`ev-step-${i}`} className="rounded-2xl border border-border bg-card p-4">
      <div className="flex items-start gap-2">
        <Input data-testid={`ev-step-label-${i}`} placeholder="Étape (ex: Cocktail, Entrée des mariés…)" value={st.label} onChange={(e) => onChange(i, "label", e.target.value)} className="flex-1" />
        <button data-testid={`ev-step-del-${i}`} onClick={() => onDelete(i)} className="rounded-md p-2 text-muted-foreground hover:bg-red-500/10 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
      </div>
      <div className="mt-2 grid gap-2 sm:grid-cols-3">
        <Input data-testid={`ev-step-date-${i}`} type="date" value={st.date || ""} onChange={(e) => onChange(i, "date", e.target.value)} />
        <Input data-testid={`ev-step-time-${i}`} type="time" value={st.time || ""} onChange={(e) => onChange(i, "time", e.target.value)} />
        <Input data-testid={`ev-step-loc-${i}`} placeholder="Lieu" value={st.location || ""} onChange={(e) => onChange(i, "location", e.target.value)} />
      </div>
      <label className="mt-2 flex items-center gap-2 text-sm text-muted-foreground">
        <input type="checkbox" data-testid={`ev-step-outdoor-${i}`} checked={!!st.outdoor} onChange={(e) => onChange(i, "outdoor", e.target.checked)} />En extérieur
        {st.outdoor && <Input data-testid={`ev-step-fallback-${i}`} placeholder="Repli en cas de pluie" value={st.fallback_location || ""} onChange={(e) => onChange(i, "fallback_location", e.target.value)} className="ml-2 h-8 flex-1" />}
      </label>
    </div>
  );
}

export function StepsTab({ steps, onChange, onDelete, onAdd, onSave, saving }) {
  return (
    <div className="space-y-3">
      {steps.map((st, i) => <StepCard key={st.id || i} st={st} i={i} onChange={onChange} onDelete={onDelete} />)}
      {!steps.length && <p className={EMPTY} data-testid="ev-steps-empty">Aucune étape. Ajoutez le déroulé minute par minute de la soirée.</p>}
      <div className="flex gap-2">
        <Button data-testid="ev-add-step" onClick={onAdd} variant="outline" className="rounded-full"><Plus className="mr-1 h-4 w-4" />Ajouter une étape</Button>
        <Button data-testid="ev-save-steps" onClick={onSave} disabled={saving} className={ORANGE_BTN}><Save className="mr-1.5 h-4 w-4" />Enregistrer le déroulé</Button>
      </div>
    </div>
  );
}
